import { DEFAULT_TIMEZONE, DELIVERABLE_STATUS_LABELS } from "@dream/config/constants";
import { handleFromName, type ResolvedOccurrence } from "@dream/domain";
import {
  Button,
  Field,
  IconPlus,
  IconTrash,
  Input,
  Skeleton,
  Textarea,
  Toggle,
  useToast,
} from "@dream/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ApiError, api, unwrap } from "../../api/client.ts";
import { useAdminCalendar } from "../../hooks/use-calendar.ts";
import {
  type CatalogClient,
  type ClientEmail,
  clientInviteEmails,
  formatInviteEmails,
  isValidEmail,
} from "../../lib/client-emails.ts";
import { formatRelativeWhen } from "../../lib/schedule-copy.ts";
import { ClientLogoField } from "../client-logo-field.tsx";
import { PinInput } from "../pin-input.tsx";

/** Ficha única: identidade, portal, e-mails da equipe e o que o cliente vê. */

interface EditorForm {
  name: string;
  handle: string;
  pin: string;
  color: string;
  notes: string;
}

export function publicClientUrl(handle: string): string {
  return `${window.location.origin}/@${handle}`;
}

export function randomClientPin(): string {
  return String(1000 + Math.floor(Math.random() * 9000));
}

function emailsFromClient(client: CatalogClient | null): ClientEmail[] {
  if (client === null) return [];
  if (client.emails !== undefined && client.emails.length > 0) {
    return client.emails.map((item) => ({
      email: item.email,
      name: item.name,
      inviteCalendar: item.inviteCalendar,
    }));
  }
  if (client.email !== null && client.email !== "") {
    return [{ email: client.email, name: "", inviteCalendar: true }];
  }
  return [];
}

export function ClientEditorDialog({
  open,
  client,
  onClose,
}: {
  open: boolean;
  client: CatalogClient | null;
  onClose: () => void;
}) {
  const isEdit = client !== null;
  const queryClient = useQueryClient();
  const toast = useToast();
  const handleDirty = useRef(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [emails, setEmails] = useState<ClientEmail[]>([]);
  const [draftEmail, setDraftEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const form = useForm<EditorForm>({
    defaultValues: { name: "", handle: "", pin: "", color: "#0074FF", notes: "" },
  });

  const watchedName = form.watch("name");
  useEffect(() => {
    if (handleDirty.current || isEdit) return;
    form.setValue("handle", watchedName.trim() === "" ? "" : handleFromName(watchedName));
  }, [watchedName, form, isEdit]);

  useEffect(() => {
    if (!open) return;
    handleDirty.current = isEdit;
    setDraftEmail("");
    setEmailError(null);
    setEmails(emailsFromClient(client));
    if (client === null) {
      form.reset({
        name: "",
        handle: "",
        pin: randomClientPin(),
        color: "#0074FF",
        notes: "",
      });
      setLogo(null);
      setLogoRemoved(false);
      return;
    }
    form.reset({
      name: client.name,
      handle: client.handle,
      pin: "",
      color: client.color,
      notes: client.notes ?? "",
    });
    setLogo(client.logoUrl ?? null);
    setLogoRemoved(false);
  }, [open, client, form, isEdit]);

  function addEmail(): void {
    const value = draftEmail.trim().toLowerCase();
    if (value === "") {
      setEmailError("Informe um e-mail");
      return;
    }
    if (!isValidEmail(value)) {
      setEmailError("Informe um e-mail válido");
      return;
    }
    if (emails.some((item) => item.email === value)) {
      setEmailError("Este e-mail já está na lista");
      return;
    }
    setEmails((current) => [...current, { email: value, name: "", inviteCalendar: true }]);
    setDraftEmail("");
    setEmailError(null);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: EditorForm) => {
      const payload = {
        name: values.name.trim(),
        color: values.color,
        handle: values.handle.trim() === "" ? undefined : values.handle.trim().toLowerCase(),
        notes: values.notes.trim() === "" ? null : values.notes.trim(),
        emails: emails.map((item) => ({
          email: item.email,
          name: item.name.trim() === "" ? null : item.name.trim(),
          inviteCalendar: item.inviteCalendar,
        })),
      };
      let saved: CatalogClient;
      if (client === null) {
        saved = unwrap(
          await api.api.admin.clients.post({
            ...payload,
            pin: values.pin,
          }),
        ) as CatalogClient;
      } else {
        saved = unwrap(
          await api.api.admin.clients({ id: client.id }).patch({
            ...payload,
            ...(values.pin.length === 4 ? { pin: values.pin } : {}),
          }),
        ) as CatalogClient;
      }
      if (logoRemoved) {
        unwrap(await api.api.admin.clients({ id: saved.id }).logo.post({ image: null }));
      } else if (logo?.startsWith("data:") === true) {
        unwrap(await api.api.admin.clients({ id: saved.id }).logo.post({ image: logo }));
      }
      return saved;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      toast.success(
        client === null ? "Cliente cadastrado" : "Cliente atualizado",
        `${saved.name} · ${publicClientUrl(saved.handle)}`,
      );
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(
        client === null ? "Não foi possível cadastrar" : "Não foi possível salvar",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const inviteList = emails.filter((item) => item.inviteCalendar).map((item) => item.email);

  return (
    <form
      className="space-y-6"
      onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))}
      noValidate
    >
      <section className="space-y-4">
        <SectionHeading
          title="Identidade"
          hint="Nome, cor e logomarca no calendário da equipe. As notas ficam só no operacional."
        />
        <Field
          label="Nome do cliente"
          htmlFor="client-name"
          required
          error={form.formState.errors.name?.message}
        >
          <Input
            id="client-name"
            autoFocus
            placeholder="Ex.: Javali Barber"
            invalid={form.formState.errors.name !== undefined}
            {...form.register("name", {
              required: "Informe o nome",
              minLength: { value: 2, message: "Mínimo de 2 caracteres" },
            })}
          />
        </Field>
        <Field label="Cor no calendário" htmlFor="client-color">
          <div className="flex items-center gap-2">
            <input
              id="client-color"
              type="color"
              className="h-9 w-14 cursor-pointer rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] p-1"
              {...form.register("color")}
            />
            <Input className="flex-1 font-mono text-[0.8125rem]" {...form.register("color")} />
          </div>
        </Field>
        <ClientLogoField
          color={form.watch("color")}
          name={form.watch("name") === "" ? "Cliente" : form.watch("name")}
          onClear={() => {
            setLogo(null);
            setLogoRemoved(true);
          }}
          onPick={(dataUrl) => {
            setLogo(dataUrl);
            setLogoRemoved(false);
          }}
          previewUrl={logoRemoved ? null : logo}
        />
        <Field label="Observações" htmlFor="client-notes" hint="Visível apenas para a equipe">
          <Textarea id="client-notes" rows={3} {...form.register("notes")} />
        </Field>
      </section>

      <section className="space-y-4">
        <SectionHeading
          title="Portal do cliente"
          hint="O cliente entra em /@handle com a senha de 4 dígitos e vê só a agenda dele."
        />
        <Field
          label="Endereço público"
          htmlFor="client-handle"
          hint="Só letras minúsculas e números, sem espaços."
          error={form.formState.errors.handle?.message}
        >
          <div className="flex items-center gap-0">
            <span className="inline-flex h-11 items-center rounded-l-[var(--radius-md)] border border-r-0 border-[var(--color-line-strong)] bg-[var(--color-surface-muted)] px-3 text-[0.875rem] font-medium text-[var(--color-ink-muted)]">
              /@
            </span>
            <Input
              id="client-handle"
              className="rounded-l-none font-mono text-[0.8125rem]"
              placeholder="javalibarber"
              invalid={form.formState.errors.handle !== undefined}
              {...form.register("handle", {
                required: "Informe o endereço",
                pattern: {
                  value: /^[a-z0-9]{3,48}$/,
                  message: "3 a 48 letras ou números, sem hífen",
                },
                onChange: () => {
                  handleDirty.current = true;
                },
              })}
            />
          </div>
        </Field>
        {isEdit ? (
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-muted)] px-2 py-1.5">
            <code className="min-w-0 flex-1 truncate text-[0.75rem] text-[var(--color-ink)]">
              {publicClientUrl(client.handle)}
            </code>
            <a
              className="inline-flex h-7 items-center rounded-lg px-2 text-[0.75rem] font-semibold text-brand-700 hover:underline"
              href={publicClientUrl(client.handle)}
              rel="noreferrer"
              target="_blank"
            >
              Abrir portal
            </a>
          </div>
        ) : null}
        <Field
          label={isEdit ? "Nova senha de 4 dígitos" : "Senha de 4 dígitos"}
          htmlFor="client-pin"
          required={!isEdit}
          hint={
            isEdit
              ? "Deixe em branco para manter a senha atual."
              : "O cliente usa esta senha para abrir a agenda. Já geramos uma — pode trocar."
          }
          error={form.formState.errors.pin?.message}
        >
          <div className="flex items-center gap-2">
            <PinInput
              id="client-pin"
              value={form.watch("pin")}
              invalid={form.formState.errors.pin !== undefined}
              onChange={(next) => {
                form.setValue("pin", next, { shouldValidate: true });
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => form.setValue("pin", randomClientPin(), { shouldValidate: true })}
            >
              Gerar
            </Button>
          </div>
          <input
            type="hidden"
            {...form.register("pin", {
              required: isEdit ? false : "Defina a senha de 4 dígitos",
              validate: (value) => value === "" || /^\d{4}$/.test(value) || "Exatamente 4 dígitos",
            })}
          />
        </Field>
      </section>

      <section className="space-y-3">
        <SectionHeading
          title="E-mails da equipe"
          hint="Quem recebe o convite automático da Google Agenda quando a entrega pede convite ao cliente. Desligue o interruptor se aquela pessoa não deve entrar no evento."
        />
        <Field
          label="E-mails da equipe"
          htmlFor="client-email-add"
          error={emailError ?? undefined}
          hint={
            inviteList.length === 0
              ? "Sem e-mail com convite ligado, a Agenda não convida ninguém do cliente."
              : `Convite padrão: ${formatInviteEmails(inviteList)}.`
          }
        >
          <div className="flex gap-2">
            <Input
              id="client-email-add"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-label="E-mail do cliente"
              placeholder="cliente@empresa.com.br"
              value={draftEmail}
              invalid={emailError !== null}
              onChange={(event) => {
                setDraftEmail(event.target.value);
                setEmailError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addEmail();
                }
              }}
            />
            <Button type="button" variant="secondary" onClick={addEmail}>
              <IconPlus className="size-4" />
              Adicionar
            </Button>
          </div>
        </Field>
        {emails.length === 0 ? (
          <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-line-strong)] px-3 py-2.5 text-[0.8125rem] text-[var(--color-ink-muted)]">
            Coloque o e-mail da empresa e o da equipe do cliente. Cada um pode entrar ou não no
            convite da Agenda.
          </p>
        ) : (
          <ul className="divide-y divide-[var(--color-line)] overflow-hidden rounded-xl border border-[var(--color-line)]">
            {emails.map((item, index) => (
              <li key={item.email} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[0.8125rem] font-semibold text-[var(--color-ink)]">
                    {item.email}
                  </p>
                  <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                    {index === 0 ? "Principal · " : ""}
                    {item.inviteCalendar ? "Convidar na Agenda" : "Fora do convite"}
                  </p>
                </div>
                <Toggle
                  aria-label={`Convidar ${item.email} na Agenda`}
                  checked={item.inviteCalendar}
                  onCheckedChange={(checked) =>
                    setEmails((current) =>
                      current.map((row) =>
                        row.email === item.email ? { ...row, inviteCalendar: checked } : row,
                      ),
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remover ${item.email}`}
                  onClick={() =>
                    setEmails((current) => current.filter((row) => row.email !== item.email))
                  }
                >
                  <IconTrash className="size-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {isEdit ? <ClientVisibilityPanel client={client} /> : null}

      <div className="flex justify-end gap-2 border-t border-[var(--color-line)] pt-4">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={saveMutation.isPending}>
          {isEdit ? "Salvar" : "Cadastrar"}
        </Button>
      </div>
    </form>
  );
}

function SectionHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h3 className="text-[0.9375rem] font-bold tracking-[0] text-[var(--color-ink)]">{title}</h3>
      <p className="mt-0.5 text-[0.75rem] leading-5 text-[var(--color-ink-muted)]">{hint}</p>
    </div>
  );
}

function ClientVisibilityPanel({ client }: { client: CatalogClient }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const range = useMemo(() => {
    const start = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf("day");
    return {
      start: start.toUTC().toISO() ?? start.toISO() ?? "",
      end: start.plus({ days: 21 }).toUTC().toISO() ?? "",
    };
  }, []);

  const calendar = useAdminCalendar({
    start: range.start,
    end: range.end,
    filters: {
      clientIds: [client.id],
      projectIds: [],
      typeIds: [],
      assigneeIds: [],
      statuses: [],
      tagIds: [],
      search: null,
    },
  });

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const list: ResolvedOccurrence[] = [];
    for (const occurrence of calendar.data?.occurrences ?? []) {
      if (seen.has(occurrence.deliverableId)) continue;
      seen.add(occurrence.deliverableId);
      list.push(occurrence);
    }
    return list;
  }, [calendar.data?.occurrences]);

  const visibility = useMutation({
    mutationFn: async (input: { id: string; version: number; visibleToClient: boolean }) =>
      unwrap(
        await api.api.admin.deliverables({ id: input.id }).patch({
          version: input.version,
          visibleToClient: input.visibleToClient,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível atualizar a visibilidade",
        error instanceof ApiError ? error.message : "Feche e abra de novo se outra pessoa salvou.",
      ),
  });

  const inviteHint = formatInviteEmails(clientInviteEmails(client));

  return (
    <section className="space-y-3">
      <SectionHeading
        title="O que o cliente vê"
        hint="Ligue para aparecer na agenda pública. Desligado fica só no operacional. Convite da Agenda é por entrega, na ficha da entrega."
      />
      {inviteHint !== "" ? (
        <p className="text-[0.75rem] leading-5 text-[var(--color-ink-secondary)]">
          E-mails com convite ligado: {inviteHint}.
        </p>
      ) : null}
      {calendar.isLoading && calendar.data === undefined ? (
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-line-strong)] px-3 py-2.5 text-[0.8125rem] text-[var(--color-ink-muted)]">
          Nenhuma entrega nos próximos 21 dias. Quando a equipe lançar, dá para escolher aqui se o
          cliente vê ou não.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-line)] overflow-hidden rounded-xl border border-[var(--color-line)]">
          {rows.map((row) => {
            const when = formatRelativeWhen(row.startsAt, row.timezone, row.isAllDay);
            return (
              <li key={row.deliverableId} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[0.8125rem] font-semibold text-[var(--color-ink)]">
                    {row.title}
                  </p>
                  <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                    {when.day} · {when.time} · {DELIVERABLE_STATUS_LABELS[row.status]}
                  </p>
                </div>
                <Toggle
                  aria-label={`Cliente vê ${row.title}`}
                  checked={row.visibleToClient}
                  disabled={visibility.isPending}
                  onCheckedChange={(checked) =>
                    visibility.mutate({
                      id: row.deliverableId,
                      version: row.version,
                      visibleToClient: checked,
                    })
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
